/**
 * Minimal i18n for the support widget (§5.12).
 *
 * Dot-path lookup with `{{var}}` interpolation, deterministic fallback to
 * English. Locale is detected from `navigator.language` at module init but
 * can be overridden via `setLocale(…)`.
 *
 * Provider-driven copy (title, intro_text, greeting, popup) is not translated
 * here — that is the provider's own content and passed through as-is.
 */

import en from './locales/en.json';
import pt from './locales/pt.json';
import es from './locales/es.json';

type Dict = Record<string, any>;

export const translations: Record<string, Dict> = { en, pt, es };
export type Locale = keyof typeof translations;

let currentLocale: Locale = detectInitialLocale();

function detectInitialLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  const raw = (navigator.language || 'en').toLowerCase().split('-')[0];
  if (raw in translations) return raw as Locale;
  return 'en';
}

export function setLocale(locale: string): void {
  if (locale in translations) {
    currentLocale = locale as Locale;
  }
}

export function getLocale(): Locale {
  return currentLocale;
}

function lookup(dict: Dict, path: string): string | undefined {
  const segments = path.split('.');
  let cur: any = dict;
  for (const seg of segments) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[seg];
  }
  return typeof cur === 'string' ? cur : undefined;
}

export function t(
  key: string,
  vars: Record<string, string | number> = {}
): string {
  let text =
    lookup(translations[currentLocale], key) ??
    lookup(translations.en, key) ??
    key;

  for (const [k, v] of Object.entries(vars)) {
    text = text.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), String(v));
  }
  return text;
}
